import { useMemo } from 'react';
import ProjectMember from '@projectMembers/models/projectMember';
import Text from '@ui/components/Text/Text';
import { Avatar, Group, Stack, Badge } from '@mantine/core';
import ProjectRole, { ProjectMemberRoleType } from '@projectMembers/models/projectRole';
import Icon from '@ui/components/Icon/Icon';
import ProjectMemberRoleSelector, { RoleOption } from '../ProjectMemberRoleSelector/ProjectMemberRoleSelector';

import styles from './ProjectMemberListItem.module.scss';

type ProjectMemberListItemProps = {
	member: ProjectMember;
	isCurrentUser: boolean;
	roleOptions: RoleOption[];
	onDelete?: ( member: ProjectMember ) => void;
	onUpdateRole?: ( member: ProjectMember, role: ProjectMemberRoleType ) => void;
	readonly?: boolean;
	showDelete?: boolean;
};

const GuestBadge = () => (
	<Badge color="gray.0" size="xs" variant="light">
		<Group spacing={4}>
			<Text variant="caption-semibold" color="gray">Guest</Text>
			<Icon icon="link" size={14} color="gray" />
		</Group>
	</Badge>
);

const YouBadge = () => (
	<Badge color="blue" size="xs" variant="light">
		<Text variant="caption-semibold" color="blue">You</Text>
	</Badge>
);

const ProjectMemberListItem = ( {
	member,
	isCurrentUser,
	roleOptions,
	onDelete,
	onUpdateRole,
	readonly = false,
	showDelete = false
}: ProjectMemberListItemProps ) => {
	const roleOptionsForMember = useMemo( () => (
		roleOptions
			.filter( option => !member.isGuest || option.role.canBeInvitedByLink() )
	), [ roleOptions, member.isGuest ] );

	return (
		<Group position="apart" className={styles.ProjectMemberListItem}>
			<Group>
				<Avatar size={34} radius="xl" src={member.user.avatar} />
				<Stack spacing={0}>
					<Group spacing={4}>
						<Text variant="body-bold">{member.user.name}</Text>
						{member.isGuest && <GuestBadge />}
						{isCurrentUser && <YouBadge />}
					</Group>
					<Text color="dark.3">{member.user.email}</Text>
				</Stack>
			</Group>
			<ProjectMemberRoleSelector
				selectedRole={member.role}
				roleOptions={roleOptionsForMember}
				readonly={readonly || isCurrentUser || member.role.id === ProjectRole.owner().id}
				showDelete={showDelete}
				onDelete={() => onDelete?.( member )}
				onChange={role => !member.hasRole( role ) && onUpdateRole?.( member, role )}
			/>
		</Group>
	);
};

export default ProjectMemberListItem;
